import React, { useMemo, useRef, useState } from "react";
import {
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import {
  ArrowForward,
  BarChart,
  ExpandMore,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Analysis } from "../../../../models/general/analysis";
import { formatAnalysisDescription } from "../../../../utils/analysis-utils";
import { formatter } from "../../../../utils/formatter-utils";
import { translate } from "../../../../translate/translate";

interface AnalysisSelectorComponentProps {
  analyses: Analysis[];
  value: Analysis | null;
  /** Id of the analysis flagged as the most recent one in the list. */
  latestId?: number;
  onChange: (analysis: Analysis) => void;
  onOpen?: (analysis: Analysis) => void;
}

const ACCENT = "#1e3f7a";
const SURFACE = "#eef2ff";
const BORDER = "#c7d4f0";
const HOVER = "#e0e8fb";

/** Keeps a long description from stretching the pill across the whole row. */
const MAX_DESCRIPTION_WIDTH = 260;

/** Floor for the dropdown, which otherwise mirrors the pill width. */
const MIN_DROPDOWN_WIDTH = 320;

// Plain styled elements instead of `Box`: App.css forces `.MuiBox-root` to
// `width: 100%`, which would stretch the pill and its trigger edge to edge.
// The pill sizes to its content but still yields on narrow viewports, so it
// never pushes the section title out of view.
const Pill = styled("div")({
  display: "inline-flex",
  alignItems: "stretch",
  width: "fit-content",
  maxWidth: "100%",
  minWidth: 0,
  border: `1px solid ${BORDER}`,
  backgroundColor: SURFACE,
  borderRadius: "8px",
  overflow: "hidden",
});

const PillTrigger = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  overflow: "hidden",
  padding: "6px 12px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  "&:hover": { backgroundColor: HOVER },
});

const AnalysisOption = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  width: "100%",
  textAlign: "left",
  padding: "6px 8px",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontFamily: "inherit",
  backgroundColor: selected ? SURFACE : "transparent",
  "&:hover": { backgroundColor: selected ? SURFACE : "#f5f5f5" },
}));

const DropdownHeader = styled("div")({ padding: "8px", borderBottom: "1px solid #f0f0f0" });
const DropdownList = styled("div")({ maxHeight: 280, overflowY: "auto", padding: "8px" });

// Same wording as the description column of the GDS listing.
const analysisLabel = (analysis: Analysis): string =>
  `#${analysis.id} · ${formatAnalysisDescription(analysis)}`;

/**
 * Analysis picker for the coverage indicators: shows the analysis currently
 * feeding the cards and lets the user switch to another completed one.
 */
const AnalysisSelectorComponent = ({
  analyses,
  value,
  latestId,
  onChange,
  onOpen,
}: AnalysisSelectorComponentProps) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>();
  const [search, setSearch] = useState("");

  // Search by number, description or customer — a typed '#' is ignored.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase().replace(/^#/, "");
    if (!term) return analyses;
    return analyses.filter((analysis) =>
      String(analysis.id).includes(term) ||
      formatAnalysisDescription(analysis).toLowerCase().includes(term) ||
      (analysis.customer || "").toLowerCase().includes(term)
    );
  }, [analyses, search]);

  // The dropdown opens under the whole pill and matches its width.
  const handleOpen = () => {
    setSearch("");
    setDropdownWidth(pillRef.current?.offsetWidth);
    setAnchorEl(pillRef.current);
  };

  if (!value) return null;

  return (
    <>
      <Pill ref={pillRef}>
        <PillTrigger
          type="button"
          onClick={handleOpen}
          title={translate.t("analysis_selector.select_analysis")}
        >
          <BarChart sx={{ fontSize: 15, color: ACCENT, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
            {translate.t("analysis_selector.indicators_from")}
          </Typography>
          <Typography
            noWrap
            title={analysisLabel(value)}
            sx={{ fontSize: "12px", fontWeight: 600, color: ACCENT, maxWidth: MAX_DESCRIPTION_WIDTH }}
          >
            {analysisLabel(value)}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
            — {formatter.formatDateTime(value.created_at ?? null)}
          </Typography>
          <ExpandMore
            sx={{
              fontSize: 15,
              color: ACCENT,
              flexShrink: 0,
              transition: "transform .2s",
              transform: anchorEl ? "rotate(180deg)" : "none",
            }}
          />
        </PillTrigger>

        <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />

        <IconButton
          size="small"
          onClick={() => onOpen?.(value)}
          title={translate.t("analysis_selector.open_analysis")}
          sx={{ borderRadius: 0, padding: "0 8px", "&:hover": { backgroundColor: HOVER } }}
        >
          <ArrowForward sx={{ fontSize: 15, color: ACCENT }} />
        </IconButton>
      </Pill>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: dropdownWidth,
              minWidth: MIN_DROPDOWN_WIDTH,
              maxWidth: "100%",
              marginTop: "4px",
              borderRadius: "8px",
            },
          },
        }}
      >
        <DropdownHeader>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={translate.t("analysis_selector.search_placeholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { fontSize: "13px" },
            }}
          />
        </DropdownHeader>

        <DropdownList>
          <Typography
            sx={{
              padding: "0 8px 6px",
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.disabled",
            }}
          >
            {translate.t("analysis_selector.completed_analyses")}
            {search.trim() ? ` · ${filtered.length}` : ""}
          </Typography>

          {filtered.length === 0 ? (
            <Typography sx={{ padding: "12px 8px", fontSize: "13px", color: "text.disabled", textAlign: "center" }}>
              {translate.t("analysis_selector.empty")}
            </Typography>
          ) : (
            filtered.map((analysis) => {
              const selected = analysis.id === value.id;
              return (
                <AnalysisOption
                  key={analysis.id}
                  type="button"
                  selected={selected}
                  onClick={() => {
                    onChange(analysis);
                    setAnchorEl(null);
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: selected ? 600 : 400,
                      color: selected ? ACCENT : "text.primary",
                    }}
                  >
                    {analysisLabel(analysis)}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                    {formatter.formatDateTime(analysis.created_at ?? null)}
                    {analysis.customer ? ` · ${analysis.customer.toUpperCase()}` : ""}
                    {analysis.id === latestId && (
                      <Typography component="span" sx={{ marginLeft: "6px", fontSize: "11px", color: ACCENT }}>
                        · {translate.t("analysis_selector.most_recent")}
                      </Typography>
                    )}
                  </Typography>
                </AnalysisOption>
              );
            })
          )}
        </DropdownList>
      </Popover>
    </>
  );
};

export default AnalysisSelectorComponent;
